/**
 * CP読取コードの生成・検証(CLAUDE.md §6)
 *
 * QRコードは次の URL を運ぶ:
 *
 *     https://<domain>/cp?i=<cp_id>&t=<token>
 *
 * このモジュールは PWA(読取側)と管理CLI(生成側)の両方が使う唯一の実装。
 * ここを分岐させると「CLIが焼いたQRをPWAが弾く」事故が起きるため、
 * 必ず両者から共有すること。
 */

import { hmacB64u, timingSafeEqual } from "./crypto";
import { normalizeKeyword, type Checkpoint } from "./checkpoints";

/** URL に載せるトークン長(base64url 文字数)。12文字 = 72bit。 */
export const TOKEN_LENGTH = 12;

/** 合言葉ハッシュ長(base64url 文字数) */
export const KEYWORD_HASH_LENGTH = 16;

/**
 * CPトークンを導出する。
 *
 * CLAUDE.md §6 の `HMAC-SHA256(secret_cp, cp_id || event_id)` に対し、
 * 区切り文字とドメイン分離プレフィックスを加えている:
 *   - 区切りなしの連結は ("0" + "1x") と ("01" + "x") が衝突し得る
 *   - 同じ secret_cp を合言葉ハッシュにも使うため、用途を前置詞で分離する
 */
export function cpTokenMessage(eventId: string, cpId: string): string {
  return `cp:${eventId}:${cpId}`;
}

export async function deriveCpToken(
  secretCp: string,
  eventId: string,
  cpId: string,
): Promise<string> {
  return hmacB64u(secretCp, cpTokenMessage(eventId, cpId), TOKEN_LENGTH);
}

export async function verifyCpToken(
  secretCp: string,
  eventId: string,
  cpId: string,
  token: string,
): Promise<boolean> {
  return timingSafeEqual(await deriveCpToken(secretCp, eventId, cpId), token);
}

/** 合言葉ハッシュを導出する(平文はバンドルに載せない) */
export async function deriveKeywordHash(
  secretCp: string,
  eventId: string,
  cpId: string,
  keyword: string,
): Promise<string> {
  const norm = normalizeKeyword(keyword);
  return hmacB64u(secretCp, `kw:${eventId}:${cpId}:${norm}`, KEYWORD_HASH_LENGTH);
}

/**
 * 合言葉を照合する。
 *
 * 注意: 合言葉の空間は3〜4文字ひらがな程度しかなく、バンドルから
 * secret_cp を抽出できる攻撃者には総当たりで破られる。これは
 * 「カメラが使えない参加者向けの救済手段」であり、防御の主体は
 * GPSジオフェンス(§9-2)とサーバー側検証(§9-4)側にある。
 */
export async function verifyKeyword(
  secretCp: string,
  eventId: string,
  cp: Pick<Checkpoint, "id" | "keyword_hash">,
  input: string,
): Promise<boolean> {
  if (!cp.keyword_hash) return false;
  const h = await deriveKeywordHash(secretCp, eventId, cp.id, input);
  return timingSafeEqual(h, cp.keyword_hash);
}

/** 読取URL を組み立てる(QRに焼く文字列) */
export function buildCpUrl(baseUrl: string, cpId: string, token: string): string {
  const u = new URL("/cp", baseUrl);
  u.searchParams.set("i", cpId);
  u.searchParams.set("t", token);
  return u.toString();
}

export interface ParsedCpCode {
  cpId: string;
  token: string;
}

/**
 * 読取結果を解析する。
 *
 * カメラのQRデコード結果・アプリ起動時の location の
 * いずれもここに通す。想定外の文字列(他所のQR等)は null を返す。
 * 相対URL や query のみの文字列も受け付ける。
 */
export function parseCpCode(raw: string): ParsedCpCode | null {
  const text = raw.trim();
  if (!text) return null;

  let params: URLSearchParams;
  try {
    // base を与えることで相対パス・query のみの入力も扱える
    const u = new URL(text, "https://placeholder.invalid");
    if (!/\/cp\/?$/.test(u.pathname) && u.pathname !== "/" && u.pathname !== "") return null;
    params = u.searchParams;
  } catch {
    return null;
  }

  const cpId = params.get("i");
  const token = params.get("t");
  if (!cpId || !token) return null;
  if (!/^\d{2}$/.test(cpId)) return null;
  if (!/^[A-Za-z0-9_-]{4,64}$/.test(token)) return null;

  return { cpId, token };
}
